<template>
  <div>
    <!-- Page Title Area -->
    <div class="page-title-area">
      <div class="container">
        <h2>{{ $t('news_title') || 'News' }}</h2>
      </div>
    </div>

    <!-- Articles List Content Area -->
    <section class="ptb-80">
      <div class="container">
        <div v-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
        <div v-else-if="error" class="alert alert-danger" role="alert">
          {{ error }}
        </div>
        <div v-else-if="articles.length === 0" class="text-center">
          <p>{{ $t('no_articles_found') || 'No articles found' }}</p>
        </div>
        <div v-else class="row">
          <div v-for="article in articles" :key="article.Key" class="col-lg-4 col-md-6 mb-4">
            <div class="single-blog-post">
              <div class="blog-image" v-if="article.Image">
                <router-link :to="`/article/${article.Key}`">
                  <img :src="article.Image" :alt="article.Title" class="img-fluid">
                </router-link>
                <div class="date" v-if="article.Date">
                  {{ formatDateShort(article.Date) }}
                </div>
              </div>
              <div class="blog-post-content">
                <h3>
                  <router-link :to="`/article/${article.Key}`">{{ article.Title }}</router-link>
                </h3>
                <p>{{ article.ShortContent }}</p>
                <router-link :to="`/article/${article.Key}`" class="read-more-btn">
                  {{ $t('read_more') || 'Read More' }} <i data-feather="arrow-right"></i>
                </router-link>
              </div>
            </div>
          </div>
          
          <!-- Pagination -->
          <div class="col-lg-12 col-md-12">
            <div class="pagination-area" v-if="pagination.page_max > 1">
              <nav aria-label="Page navigation">
                <ul class="pagination justify-content-center">
                  <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                    <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)" aria-label="Previous">
                      <i data-feather="chevron-left"></i>
                    </a>
                  </li>
                  <li v-for="page in paginationPages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                    <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage >= pagination.page_max }">
                    <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)" aria-label="Next">
                      <i data-feather="chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchCmsEntries } from '../utils/api'
import i18next from '../i18next'
import feather from 'feather-icons'

export default {
  name: 'ArticleListView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const articles = ref([])
    const loading = ref(true)
    const error = ref(null)
    const pagination = ref({})
    const currentPage = ref(1)
    
    // Computed property for pagination display
    const paginationPages = computed(() => {
      const totalPages = pagination.value.page_max || 1
      const current = currentPage.value
      const pages = []
      
      // Always show first page, last page, current page, and 1 page before and after current
      if (totalPages <= 5) {
        // If 5 or fewer pages, show all
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // Always show first page
        pages.push(1)
        
        // Show ellipsis before middle section if needed
        if (current > 3) {
          pages.push('...')
        }
        
        // Middle section around current page
        const start = Math.max(2, current - 1)
        const end = Math.min(totalPages - 1, current + 1)
        for (let i = start; i <= end; i++) {
          pages.push(i)
        }
        
        // Show ellipsis after middle section if needed
        if (current < totalPages - 2) {
          pages.push('...')
        }
        
        // Always show last page
        if (totalPages > 1) {
          pages.push(totalPages)
        }
      }
      
      return pages
    })

    const formatDateShort = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short', 
        day: 'numeric'
      })
    }

    const changePage = (page) => {
      if (page === '...' || page < 1 || page > pagination.value.page_max) {
        return
      }
      
      currentPage.value = page
      
      // Update URL with query parameter
      router.push({
        path: route.path,
        query: { ...route.query, page }
      })
      
      fetchArticles(page)
    }

    const fetchArticles = async (page = 1) => {
      loading.value = true
      error.value = null
      
      try {
        console.log(`Fetching articles list, page ${page}`)
        
        const params = {
          page,
          limit: 9,
          sort: 'published:desc',
          query: {
            status: 'published'
          }
        }
        
        // Add category filter if specified in URL
        if (route.query.category) {
          params.query.category = route.query.category
        }
        
        // Add search filter if specified in URL
        if (route.query.search) {
          params.query.title = route.query.search
        }
        
        const result = await fetchCmsEntries('article', params)
        
        if (result && result.items) {
          articles.value = result.items.map(article => {
            // Process date information
            if (article.Published && typeof article.Published === 'object') {
              article.Date = article.Published.iso
            } else if (article.Created && typeof article.Created === 'object') {
              article.Date = article.Created.iso
            }
            return article
          })
          pagination.value = result.pagination || {}
        } else {
          console.warn('No articles found in response:', result)
          articles.value = []
        }
      } catch (err) {
        console.error('Error fetching articles:', err)
        if (err.response && err.response.status >= 500) {
          error.value = i18next.t('server_error') || 'Server error when loading articles. Please try again later.'
        } else {
          error.value = i18next.t('error_loading_articles') || 'Error loading articles'
        }
      } finally {
        loading.value = false
        
        // Initialize feather icons after content loads
        setTimeout(() => {
          feather.replace()
        }, 100)
        
        // Scroll to top when changing pages
        window.scrollTo(0, 0)
      }
    }

    onMounted(() => {
      // Get page from URL if present
      const pageParam = parseInt(route.query.page) || 1
      currentPage.value = pageParam
      
      fetchArticles(pageParam)
      
      // Set page title
      document.title = i18next.t('news_title') || 'Articles - AtOnline.com'
    })

    // Watch for route query changes (for filters)
    watch(() => route.query, (newQuery, oldQuery) => {
      if (newQuery.category !== oldQuery.category || newQuery.search !== oldQuery.search) {
        currentPage.value = 1
        fetchArticles(1)
      }
    }, { deep: true })

    // For i18n
    const $t = (key) => i18next.t(key)

    return {
      articles,
      loading,
      error,
      pagination,
      currentPage,
      paginationPages,
      formatDateShort,
      changePage,
      $t
    }
  }
}
</script>

<style scoped>
.pagination-area {
  margin-top: 30px;
}

.single-blog-post {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.blog-post-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.read-more-btn {
  margin-top: auto;
}
</style>